import { TrackedEvent } from "@/domains/metrics";
import { trackEvent } from "@/domains/metrics";
import { FileDropItemWithPath } from "@/modules/file/dropItems";
import { pluralize } from "@/modules/pluralize";
import { AppStore } from "@/store";
import {
  markImportSessionInProgress,
  clearImportSessionInProgress,
} from "@/modules/cache/import-session";
import { objectModule } from "@/modules/object";

export const importFilesAsSession = async (files: FileDropItemWithPath[], store: AppStore) => {
  const start = performance.now();
  const droppedFilesCount = files.length;

  // Create a session for this import
  const sessionId = await store.sessions.createSession({
    title: `Bring in ${droppedFilesCount} ${pluralize(droppedFilesCount, "file", "files")}`,
    status: "PROCESSING",
    kind: "IMPORT",
  });

  // Track in-progress import for refresh safety
  await markImportSessionInProgress(sessionId);

  // Create a file batch for these files
  const batchId = await store.uploadedFileBatches.createUploadedFileBatch({
    sessionId,
    //config: { create_collections_from_tags: true }, // TODO: remove once we're including this as an Input Request event
  });

  // Upload files
  store.sessionEvents.createMessageSentSessionEvent({
    session_id: sessionId,
    message_value: {
      kind: "REASONING",
      content: `Uploading ${droppedFilesCount} ${pluralize(droppedFilesCount, "file", "files")}`,
    },
  });

  // Upload all files. If any fail, mark session as errored.
  let uploadedFileIds: string[] = [];
  try {
    uploadedFileIds = await store.uploadProgress.uploadFileItems(files, sessionId);
  } catch (e) {
    // Mark session as errored
    await store.sessions.updateSessionOnNewUserMessage(sessionId, { status: "ERROR" });
    await clearImportSessionInProgress(sessionId);

    store.sessionEvents.createMessageSentSessionEvent({
      session_id: sessionId,
      message_value: {
        kind: "REASONING",
        content: "Failed to upload files",
      },
    });

    trackEvent(TrackedEvent.ImportFilesBatchFailed, {
      num_files: droppedFilesCount,
      session_id: sessionId,
      context: "importFilesAsSession",
      error: objectModule.safeErrorAsJson(e as Error),
    });

    throw e;
  } finally {
    await clearImportSessionInProgress(sessionId);
  }

  // Create File Upload models using accumulated results
  const uploadedFileResults = store.uploadProgress.getFileResults(uploadedFileIds);
  await store.uploadedFiles.createUploadedFiles({ batchId, uploadedFileResults });

  // Add all uploads to the batch
  await store.uploadedFileBatches.addUploadsToBatch(batchId, uploadedFileIds);

  // Start processing the batch
  await store.uploadedFileBatches.startProcessingBatch(batchId, sessionId);

  // Track event
  trackEvent(TrackedEvent.ImportFilesBatchStarted, {
    num_files: uploadedFileResults.length,
    batch_id: batchId,
    session_id: sessionId,
    upload_duration_s: (performance.now() - start) / 1000,
  });
};
